import React, { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../../context/SubscriptionContext';
import { PUBLIC_PATHS, SUBSCRIPTION_PATHS } from '../../utils/constants';
import '../../styles/SubscriptionModal.css';

/**
 * SubscriptionModal Component
 *
 * Blocking modal shown when app is locked.
 * Shows when:
 * - User has no subscription (redirects to plans page to choose)
 * - User's subscription expired AND grace period is over
 *
 * Does NOT show when:
 * - User is in grace period (GracePeriodWarning handles this)
 * - User has active subscription
 * - User is on subscription-related or public pages
 */
const SubscriptionModal = () => {
    const { t } = useTranslation(['subscription', 'common']);
    const navigate = useNavigate();
    const location = useLocation();

    // Context
    const {
        subscriptionData,
        isLocked,
        canStartTrial,
        getSubscriptionStatusText,
        formatDate,
        loading: contextLoading
    } = useSubscription();

    // Local state
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

    /**
     * Check if modal should be displayed
     */
    const shouldShowModal = useCallback(() => {
        // Don't show while loading
        if (contextLoading) return false;

        // Don't show on public/auth pages
        if (PUBLIC_PATHS.includes(location.pathname)) return false;

        // Don't show on subscription pages
        if (SUBSCRIPTION_PATHS.some(path => location.pathname.includes(path))) return false;

        // Show if locked
        return isLocked();
    }, [contextLoading, location.pathname, isLocked]);

    /**
     * Handle navigating to plans page
     */
    const handleViewPlans = useCallback(() => {
        navigate('/subscription/plans');
    }, [navigate]);

    /**
     * Handle logout
     */
    const handleLogout = useCallback(() => {
        localStorage.removeItem('jwt');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userType');
        sessionStorage.clear();
        navigate('/');
    }, [navigate]);

    // Don't render if not needed
    if (!shouldShowModal()) {
        return null;
    }

    const canTrial = canStartTrial();
    const status = subscriptionData?.status;

    return (
        <>
            {/* Main Modal */}
            <div className="subscription-modal-overlay">
                <div className="subscription-modal">
                    {/* Header */}
                    <div className="modal-header">
                        <div className="modal-icon">
                            {canTrial ? '🚀' : '🔒'}
                        </div>
                        <h2>
                            {canTrial
                                ? t('welcome.title')
                                : t('welcome.subtitle')}
                        </h2>
                    </div>

                    {/* Body */}
                    <div className="modal-body">
                        {canTrial ? (
                            <TrialContent />
                        ) : (
                            <ExpiredContent
                                subscriptionData={subscriptionData}
                                status={status}
                                getStatusText={getSubscriptionStatusText}
                                formatDate={formatDate}
                            />
                        )}
                    </div>

                    {/* Actions */}
                    <div className="modal-actions">
                        <button
                            className="primary-btn"
                            onClick={handleViewPlans}
                        >
                            {canTrial ? t('selectPlanAndTrial') : t('selectPlan')}
                        </button>

                        <button
                            className="text-btn logout-btn"
                            onClick={() => setShowLogoutConfirm(true)}
                        >
                            {t('modal.logout')}
                        </button>
                    </div>

                    {/* Footer */}
                    <div className="modal-footer">
                        <p>
                            {t('modal.autoCloseMessage')}
                        </p>
                    </div>
                </div>
            </div>

            {/* Logout Confirmation Modal */}
            {showLogoutConfirm && (
                <LogoutConfirmModal
                    onConfirm={handleLogout}
                    onCancel={() => setShowLogoutConfirm(false)}
                />
            )}
        </>
    );
};

/**
 * Trial Content - shown for new users who can start trial
 */
const TrialContent = () => {
    const { t } = useTranslation('subscription');
    return (
        <>
            <p className="modal-message"
               dangerouslySetInnerHTML={{ __html: t('welcome.messageHtml') }}
            />

            <div className="trial-benefits">
                <h4>{t('welcome.whatYouGet')}</h4>
                <ul>
                    <li>{t('welcome.freeAccess')}</li>
                    <li>{t('welcome.allFeatures')}</li>
                    <li>{t('welcome.noCommitment')}</li>
                    <li>{t('welcome.techSupport')}</li>
                </ul>
            </div>
        </>
    );
};

/**
 * Expired Content - shown when subscription has expired
 */
const ExpiredContent = ({ subscriptionData, status, getStatusText, formatDate }) => {
    const { t } = useTranslation('subscription');
    return (
        <>
            <p className="modal-message">
                {t('expired.renewMessage')}
            </p>

            {subscriptionData && (
                <div className="subscription-info">
                    <p>
                        <strong>{t('expired.status')}</strong>{' '}
                        {getStatusText(status)}
                    </p>
                    {subscriptionData.endDate && (
                        <p>
                            <strong>{t('expired.expirationDate')}</strong>{' '}
                            {formatDate(subscriptionData.endDate)}
                        </p>
                    )}
                </div>
            )}

            <div className="locked-features">
                <h4>{t('expired.lockedFeaturesTitle')}</h4>
                <div className="features-grid">
                    <span>{t('welcome.trainingCalendar')}</span>
                    <span>{t('welcome.dogManagement')}</span>
                    <span>{t('welcome.trainingNotes')}</span>
                    <span>{t('welcome.reportsAndAnalytics')}</span>
                </div>
            </div>
        </>
    );
};

/**
 * Logout Confirmation Modal
 */
const LogoutConfirmModal = ({ onConfirm, onCancel }) => {
    const { t } = useTranslation(['subscription', 'common']);
    return (
        <div className="logout-confirm-overlay">
            <div className="logout-confirm-modal">
                <h3>{t('subscription:modal.logoutConfirmTitle')}</h3>
                <p>{t('subscription:modal.logoutConfirmMessage')}</p>
                <div className="logout-confirm-actions">
                    <button onClick={onConfirm}>{t('subscription:modal.logoutConfirm')}</button>
                    <button onClick={onCancel}>{t('common:cancel')}</button>
                </div>
            </div>
        </div>
    );
};

export default SubscriptionModal;
